import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useMarketData, MarketDataItem } from './MarketDataContext';
import { useValorePrices } from './ValorePriceContext';
import { useBybitPrices } from './BybitPriceContext';
import { isValoreSupported } from '../constants/valoreSymbols';
import { getListedCfdInstrument } from '../../supabase/functions/_shared/cfdInstruments';

const normalizeSymbol = (symbol: string): string => {
  return symbol.replace('/', '');
};

const denormalizeSymbol = (symbol: string): string => {
  if (symbol.length === 6 && !symbol.includes('/')) {
    return `${symbol.slice(0, 3)}/${symbol.slice(3)}`;
  }
  return symbol;
};

const isCryptoSymbol = (symbol: string): boolean => {
  return symbol.endsWith('USDT') || symbol.endsWith('USD') && !symbol.includes('/');
};

interface UnifiedPriceContextType {
  getPriceBySymbol: (symbol: string) => number;
  getBidPrice: (symbol: string) => number;
  getAskPrice: (symbol: string) => number;
  getPriceData: (symbol: string) => { bid: number; ask: number; mid: number } | null;
  getMarketData: (symbol: string) => MarketDataItem | null;
  isConnected: boolean;
  valoreConnected: boolean;
  bybitConnected: boolean;
  supabaseConnected: boolean;
  lastUpdateTime: number;
  marketData: MarketDataItem[];
}

const UnifiedPriceContext = createContext<UnifiedPriceContextType | undefined>(undefined);

interface UnifiedPriceProviderProps {
  children: React.ReactNode;
}

export const UnifiedPriceProvider: React.FC<UnifiedPriceProviderProps> = ({ children }) => {
  const {
    getPriceBySymbol: getSupabasePrice,
    getMarketDataBySymbol,
    marketData,
    isConnected: supabaseConnected
  } = useMarketData();

  const {
    getPriceBySymbol: getValorePrice,
    getBidPrice: getValoreBid,
    getAskPrice: getValoreAsk,
    getPriceData: getValorePriceData,
    isConnected: valoreConnected,
    lastUpdateTime: valoreLastUpdate
  } = useValorePrices();

  const {
    getPriceBySymbol: getBybitPrice,
    getBidPrice: getBybitBid,
    getAskPrice: getBybitAsk,
    getPriceData: getBybitPriceData,
    isConnected: bybitConnected,
    lastUpdateTime: bybitLastUpdate
  } = useBybitPrices();

  const getPriceBySymbol = useCallback((symbol: string): number => {
    // MarketDataContext selects the newer of the VPS stream and its DB mirror.
    if (getListedCfdInstrument(symbol)) return getSupabasePrice(symbol);
    const isCrypto = isCryptoSymbol(symbol);

    if (isCrypto) {
      const bybitPrice = getBybitPrice(symbol);
      if (bybitPrice > 0) {
        return bybitPrice;
      }
    }

    const isValore = isValoreSupported(symbol);
    if (isValore) {
      const valorePrice = getValorePrice(symbol);
      if (valorePrice > 0) {
        return valorePrice;
      }
    }

    let price = getSupabasePrice(symbol);
    if (price > 0) {
      return price;
    }

    const normalized = normalizeSymbol(symbol);
    if (normalized !== symbol) {
      price = getSupabasePrice(normalized);
      if (price > 0) {
        return price;
      }
    }

    const denormalized = denormalizeSymbol(symbol);
    if (denormalized !== symbol) {
      price = getSupabasePrice(denormalized);
      if (price > 0) {
        return price;
      }
    }

    return 0;
  }, [getBybitPrice, getValorePrice, getSupabasePrice, valoreLastUpdate, bybitLastUpdate, valoreConnected, bybitConnected]);

  const getBidPrice = useCallback((symbol: string): number => {
    if (getListedCfdInstrument(symbol)) {
      const quote = getMarketDataBySymbol(symbol);
      return quote?.bid_price || quote?.price || 0;
    }
    if (isCryptoSymbol(symbol)) {
      const bid = getBybitBid(symbol);
      if (bid > 0) return bid;
    }

    if (isValoreSupported(symbol)) {
      const bid = getValoreBid(symbol);
      if (bid > 0) return bid;
    }

    let marketItem = getMarketDataBySymbol(symbol);
    if (marketItem?.bid_price || marketItem?.price) {
      return marketItem.bid_price || marketItem.price || 0;
    }

    const normalized = normalizeSymbol(symbol);
    if (normalized !== symbol) {
      marketItem = getMarketDataBySymbol(normalized);
      if (marketItem?.bid_price || marketItem?.price) {
        return marketItem.bid_price || marketItem.price || 0;
      }
    }

    return 0;
  }, [getBybitBid, getValoreBid, getMarketDataBySymbol, valoreLastUpdate, bybitLastUpdate]);

  const getAskPrice = useCallback((symbol: string): number => {
    if (getListedCfdInstrument(symbol)) {
      const quote = getMarketDataBySymbol(symbol);
      return quote?.ask_price || quote?.price || 0;
    }
    if (isCryptoSymbol(symbol)) {
      const ask = getBybitAsk(symbol);
      if (ask > 0) return ask;
    }

    if (isValoreSupported(symbol)) {
      const ask = getValoreAsk(symbol);
      if (ask > 0) return ask;
    }

    let marketItem = getMarketDataBySymbol(symbol);
    if (marketItem?.ask_price || marketItem?.price) {
      return marketItem.ask_price || marketItem.price || 0;
    }

    const normalized = normalizeSymbol(symbol);
    if (normalized !== symbol) {
      marketItem = getMarketDataBySymbol(normalized);
      if (marketItem?.ask_price || marketItem?.price) {
        return marketItem.ask_price || marketItem.price || 0;
      }
    }

    return 0;
  }, [getBybitAsk, getValoreAsk, getMarketDataBySymbol, valoreLastUpdate, bybitLastUpdate]);

  const getPriceData = useCallback((symbol: string): { bid: number; ask: number; mid: number } | null => {
    if (getListedCfdInstrument(symbol)) {
      const quote = getMarketDataBySymbol(symbol);
      return quote && quote.price > 0
        ? { bid: quote.bid_price || quote.price, ask: quote.ask_price || quote.price, mid: quote.price } : null;
    }
    if (isCryptoSymbol(symbol)) {
      const data = getBybitPriceData(symbol);
      if (data) {
        return { bid: data.bid, ask: data.ask, mid: data.price };
      }
    }

    if (isValoreSupported(symbol)) {
      const data = getValorePriceData(symbol);
      if (data) {
        return { bid: data.bid, ask: data.ask, mid: data.mid };
      }
    }

    let marketItem = getMarketDataBySymbol(symbol);
    if (marketItem && marketItem.price > 0) {
      const price = marketItem.price || 0;
      return {
        bid: marketItem.bid_price || price,
        ask: marketItem.ask_price || price,
        mid: price
      };
    }

    const normalized = normalizeSymbol(symbol);
    if (normalized !== symbol) {
      marketItem = getMarketDataBySymbol(normalized);
      if (marketItem && marketItem.price > 0) {
        const price = marketItem.price || 0;
        return {
          bid: marketItem.bid_price || price,
          ask: marketItem.ask_price || price,
          mid: price
        };
      }
    }

    return null;
  }, [getBybitPriceData, getValorePriceData, getMarketDataBySymbol, valoreLastUpdate, bybitLastUpdate]);

  const getMarketData = useCallback((symbol: string): MarketDataItem | null => {
    let data = getMarketDataBySymbol(symbol);
    if (data) return data;

    const normalized = normalizeSymbol(symbol);
    if (normalized !== symbol) {
      data = getMarketDataBySymbol(normalized);
      if (data) return data;
    }

    return null;
  }, [getMarketDataBySymbol]);

  const contextValue: UnifiedPriceContextType = useMemo(() => ({
    getPriceBySymbol,
    getBidPrice,
    getAskPrice,
    getPriceData,
    getMarketData,
    isConnected: valoreConnected || bybitConnected || supabaseConnected,
    valoreConnected,
    bybitConnected,
    supabaseConnected,
    lastUpdateTime: Math.max(valoreLastUpdate, bybitLastUpdate),
    marketData
  }), [
    getPriceBySymbol,
    getBidPrice,
    getAskPrice,
    getPriceData,
    getMarketData,
    valoreConnected,
    bybitConnected,
    supabaseConnected,
    valoreLastUpdate,
    bybitLastUpdate,
    marketData
  ]);

  return (
    <UnifiedPriceContext.Provider value={contextValue}>
      {children}
    </UnifiedPriceContext.Provider>
  );
};

export const useUnifiedPrices = (): UnifiedPriceContextType => {
  const context = useContext(UnifiedPriceContext);
  if (context === undefined) {
    throw new Error('useUnifiedPrices must be used within a UnifiedPriceProvider');
  }
  return context;
};
